<template>
  <content-subpage
    :visible="visible"
    :titles="titles"
    :content-title="contentTitle"
    class="resource-copy-content-subpage"
    @content-subpage-hidden="actionHidden(params.actionHidden)"
    @content-subpage-back="actionBack()"
  >
    <template #main-content>
      <overlay-loader v-if="isLoading" />
      <template v-else>
        <cv-form v-if="showTitle">
          <dl class="field-set description">
            <dt class="field-label">{{ t("title") }}</dt>
            <dd class="field-input">
              <text-input
                ref="titleTextInput"
                :value="titleInput"
                :mandatory="true"
                :character-limit="titleCharacterLimit"
                :error-message="errorMessage"
                :text-id="dirtyId"
                @input="handleTitleChange"
              />
            </dd>
          </dl>
        </cv-form>
        <resource-listing :type="params.type" @on-resource-selected="handleResourceSelected" />
      </template>
    </template>
    <template #action-button>
      <fetch-data-button
        class="move-selected-btn"
        :kind="dataSaveButton.kind"
        :error="dataSaveButton.error"
        :size="dataSaveButton.size"
        :loading="dataSaveButton.loading"
        :error-message="dataSaveButton.errorMessage"
        :disabled="isDisabled"
        tip-position="top"
        tip-alignment="end"
        @click="handleCopyResource"
        @reset-error-state="handleSaveButtonResetError"
        @reset-completed-state="handleSaveButtonResetCompleted"
      >
        <template v-if="dataSaveButton.error">
          {{ t("button_state_error") }}
        </template>
        <template v-else>
          <svg-icon name="Copy32" kind="action" />
          <span>{{ t("copy") }}</span>
        </template>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>

<script lang="ts" setup>
import { isDefined } from "@utils/var.check";
import { RESOURCE_TYPE } from "@plugins/common/constant/resource.constant.ts";
import { COPY_ACTION_TYPE } from "@modules/my-drive/my-drive-helper.ts";
import { DATA_STATE } from "@plugins/modal/modal.constant";
import { loadLocaleMessages } from "@plugins/i18n";
import MyDriveWaitMixin from "@modules/my-drive/mixin/mydrive.wait.mixin";
import ResourceListing from "@modules/my-drive/components/ResourceListing.vue";
import MyDriveApi from "@apis/my.drive.api";
import lessonApi from "@apis/editor.lesson.api";
import assignmentApi from "@apis/assignment.api";
import PollingUtils from "@utils/polling.utils";
import { ERROR_CODE } from "@plugins/common/constant/error.constant";
import { RESOURCE_DOMAIN_PATH } from "@plugins/common/constant/resource.constant";
import EditorNavigationMixin from "@modules/editor/mixin/editor.navigation.mixin";
import lessonEditHistoryApi from "@apis/lesson.edit.open.history.api";
import { ref, computed, onBeforeUnmount, useTemplateRef } from "vue";
import { useVm } from "@utils/vm.ts";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useContentSubpage } from "@/plugins/content-subpage/composables/content-subpage";
import { useProcessingState } from "@/modules/app/composables/processing-state";
import { useModal } from "@/plugins/modal/composables/modal-instance";
import { useToast } from "@/plugins/toast/composables/toast-instance";
import { useRouter } from "vue-router";
import { useErrorHandler } from "@/plugins/common/composables/error";

defineOptions({
  mixins: [MyDriveWaitMixin, EditorNavigationMixin],
});

const emit = defineEmits(["onResourceSelected", "onResourceDuplicated"]);

// Data
const selected = ref([]);
const title = ref("");
const errorMessage = ref("");
const titleCharacterLimit = ref(250);
const titleInput = ref("");
const isLoading = ref(true);
const resourceSelected = ref("");
const copyFunction = ref(null);
const newLessonUuid = ref("");
const titleTextInput = useTemplateRef("titleTextInput");
const modalHandler = useModal();
const toastHandler = useToast();
// Props
const props = defineProps({
  params: { type: Object, required: true },
});

const dirtyId = ref("ResourceListing");

const {
  titles,
  visible,
  dataSaveButton,
  setDirty,
  clearDirty,
  setDataState,
  actionBack,
  actionHidden,
  handleSaveButtonResetError,
  handleSaveButtonResetCompleted,
  defaultExpose,
} = useContentSubpage(props.params, dirtyId);

const store = useStore();
const { vm, vmExpose } = useVm();
const { t } = useI18n();
const { setProcessing, resetProcessing } = useProcessingState();
const router = useRouter();
const { passiveApiFailHandler } = useErrorHandler();
defineExpose(defaultExpose);

const isTeacher = computed(() => store.getters["auth/isTeacher"]);

// Computed

const showTitle = computed(() => {
  return props.params.resources[0].type !== RESOURCE_TYPE.QUESTION;
});

const contentTitle = computed(() => {
  return isDefined(props.params.customTitle) ? props.params.customTitle : t("resource_copy_title");
});

const waitActionPrimary = computed(() => {
  return props.params.waitActionPrimary;
});

const itemType = computed(() => {
  return "course";
});

// Methods
const handleTitleChange = function (value) {
  setDirty(dirtyId.value);
  errorMessage.value = "";
  titleInput.value = value;
};

const validate = function () {
  clearErrorMessage();
  return titleTextInput.value.validate();
};

const clearErrorMessage = function () {
  errorMessage.value = "";
};

const handleCopyResource = function () {
  if (isDisabled.value) {
    return;
  }
  // Pass source lesson metadata so MyDriveWaitModal can show the upvote prompt
  vm.lessonUuid = props.params.resources[0]?.uuid ?? null;
  vm.resourceDomain = props.params.resources[0]?.resourceDomain ?? null;
  setProcessing("save");
  if (validate()) {
    switch (props.params.actionType) {
      // BY TEACHER
      case COPY_ACTION_TYPE.COPY_NODE: {
        copyFunction.value = handleCopyResourceNode;
        handleCopyResourceNode();
        break;
      }
      // BY TEACHER
      case COPY_ACTION_TYPE.COPY_LESSON: {
        copyFunction.value = handleCopyResourceLesson;
        handleCopyResourceLesson();
        break;
      }
      // BY STUDENT
      case COPY_ACTION_TYPE.REATTEMPT_ASSIGNMENT_LESSON: {
        copyFunction.value = handleCopyAssignmentLesson;
        handleCopyAssignmentLesson();
        break;
      }
      // BY TEACHER, STUDENT
      case COPY_ACTION_TYPE.COPY_ASSIGNMENT_LESSON: {
        copyFunction.value = handleCopyAssignmentLesson;
        handleCopyAssignmentLesson();
        break;
      }
      // BY STUDENT
      case COPY_ACTION_TYPE.COPY_MOE_LESSON: {
        copyFunction.value = handleCopyMOELesson;
        handleCopyMOELesson();
        break;
      }
      default: {
        break;
      }
    }
  } else {
    resetProcessing();
  }
};

const handleCopyResourceNode = function () {
  const requestData = {
    resourceNodeUuid: props.params.resources[0].fileUuid,
    targetFolderUuid: selected.value[0].fileUuid,
    title: titleInput.value,
  };
  setDataState(DATA_STATE.LOADING);
  vm.initializeTimer("duplicating");
  resourceSelected.value = selected.value[0].fileUuid;

  //use polling
  MyDriveApi.copyResource(requestData).then(
    (response) => {
      if (response.code !== "ok") {
        handleApiError(response);
        return;
      }

      //OK
      const duplicationUuid = response.data;
      newLessonUuid.value = duplicationUuid;
      getDuplicationStatus(duplicationUuid).then((response) => {
        if (response.code !== "ok") {
          handleApiError(response);
          return;
        }

        if (vm.readingTime >= vm.MAX_RESPONSE_TIME && response.data === true) {
          handleDuplicated(response);
        } else if (response.code === "ok" && response.data === true) {
          // show success modal if done within max response time
          vm.resetTimer();
          handleDuplicated(response);
          emit("onResourceSelected", resourceSelected.value);
          vm.showResourceCopyModal();
        }
      });
    },
    () => {
      handleApiError();
    },
  );
};

const handleCopyResourceLesson = function () {
  const requestData = {
    resourceUuid: props.params.resources[0].uuid,
    targetFolderUuid: selected.value[0].fileUuid,
    title: titleInput.value,
  };
  setDataState(DATA_STATE.LOADING);
  vm.initializeTimer("duplicating");
  resourceSelected.value = selected.value[0];

  //use polling
  lessonApi.copyLesson(requestData).then((response) => {
    if (response.code !== "ok") {
      handleApiError(response);
      return;
    }

    //OK
    const duplicationUuid = response.data.nodeUuid;
    newLessonUuid.value = duplicationUuid;
    getDuplicationStatus(duplicationUuid).then((response) => {
      if (response.code !== "ok") {
        handleApiError(response);
        return;
      }

      if (vm.readingTime >= vm.MAX_RESPONSE_TIME && response.data === true) {
        handleDuplicated(response);
      } else if (response.code === "ok" && response.data === true) {
        // show success modal if done within max response time
        vm.resetTimer();
        handleDuplicated(response);
        emit("onResourceSelected", resourceSelected.value);
        vm.showResourceCopyModal();
      }
    });
  });
};

const showUnableToCopy = function () {
  //show modal window
  const props = {
    title: t("assignment_header_right_unable_to_copy_to_drive_title"),
    content: t("assignment_header_right_unable_to_copy_to_drive_processing_text"),
    primaryBtnLabel: t("button_ok"),
    use_secondaryButton: false,
  };
  modalHandler.showMessage("warning", props);
};

const handleCopyAssignmentLesson = function () {
  const requestData = {
    resourceUuid: props.params.resources[0].uuid,
    targetFolderUuid: selected.value[0].fileUuid,
    title: titleInput.value,
  };
  setDataState(DATA_STATE.LOADING);
  vm.initializeTimer("duplicating");
  resourceSelected.value = selected.value[0];

  //use polling
  assignmentApi.copyAssignmentLesson(requestData).then((response) => {
    if (response.code !== "ok") {
      handleApiError(response);
      return;
    }

    //OK
    const duplicationUuid = response.data;
    newLessonUuid.value = duplicationUuid;
    getDuplicationStatus(duplicationUuid).then((response) => {
      if (response.code !== "ok") {
        handleApiError(response);
        return;
      }

      if (isTeacher.value) {
        if (vm.readingTime >= vm.MAX_RESPONSE_TIME && response.data === true) {
          handleDuplicated(response);
        } else if (response.code === "ok" && response.data === true) {
          // show success modal if done within max response time
          vm.resetTimer();
          handleDuplicated(response);
          vm.showResourceCopyModal();
        }
      } else {
        if (vm.readingTime >= vm.MAX_RESPONSE_TIME && response.data === true) {
          handleDuplicated(response);
          vm.showMyDriveWaitModal(waitActionPrimary.value);
        } else if (response.code === "ok" && response.data === true) {
          vm.resetTimer();
          setTimeout(() => {
            handleDuplicated(response);
            toastHandler.show("success", {
              title: t("copy_lesson_success_toast"),
            });
            if (props.params.actionType == COPY_ACTION_TYPE.REATTEMPT_ASSIGNMENT_LESSON) {
              emit("onResourceDuplicated", duplicationUuid);
            } else if (props.params.actionType == COPY_ACTION_TYPE.COPY_ASSIGNMENT_LESSON) {
              emit("onResourceSelected", resourceSelected.value);
            }
            actionBack();
          }, 3000);
        }
      }
    });
  });
};

const handleApiError = function (response) {
  vm.resetTimer();
  if (response && response?.errorMap[0] === ERROR_CODE.FILE_PROCESSING) {
    showUnableToCopy();
  } else {
    resetProcessing();
    setDataState(DATA_STATE.ERROR);
  }
};

const handleCopyMOELesson = function () {
  const requestData = {
    resourceUuid: props.params.resources[0].uuid,
    targetFolderUuid: selected.value[0].fileUuid,
    title: titleInput.value,
  };
  setDataState(DATA_STATE.LOADING);
  vm.initializeTimer("duplicating");
  resourceSelected.value = selected.value[0];

  //use polling
  lessonApi.copyMOELesson(requestData).then((response) => {
    if (response.code !== "ok") {
      handleApiError(response);
      return;
    }

    //OK
    const duplicationUuid = response.data;
    getDuplicationStatus(duplicationUuid).then((response) => {
      if (response.code !== "ok") {
        handleApiError(response);
        return;
      }

      if (vm.readingTime >= vm.MAX_RESPONSE_TIME && response.data === true) {
        handleDuplicated(response);
        vm.showMyDriveWaitModal(waitActionPrimary.value);
      } else if (response.code === "ok" && response.data === true) {
        vm.resetTimer();
        setTimeout(() => {
          handleDuplicated(response);
          toastHandler.show("success", {
            title: t("copy_lesson_success_toast"),
          });
          emit("onResourceSelected", resourceSelected.value);
          actionBack();
        }, 3000);
      }
    });
  });
};

const getDuplicationStatus = function (uuid) {
  return new Promise((resolve, reject) => {
    PollingUtils.pollingWithFibonacciInterval(
      uuid,
      lessonApi.getAsyncDuplicationStatus,
      continueCheckingForCopiedStatus,
      Date.now(),
      300000,
      1000,
      2000,
    ).then(
      (response) => {
        resolve(response);
      },
      () => {
        // error ignored, no handling
        reject();
      },
    );
  });
};

const continueCheckingForCopiedStatus = function (response) {
  if (response.code === "ok" && response.data === true) {
    return false;
  }
  return !vm.isRedirect;
};

const handleResourceSelected = function (newSelected) {
  selected.value = newSelected;
};

const isDisabled = computed(() => {
  return selected.value.length === 0 || dataSaveButton.value.loading;
});

const handleDuplicated = function (response) {
  vm.apiResponse = response;
  vm.status = "duplicated";
  resetProcessing();
  clearDirty();
  setDataState(DATA_STATE.COMPLETED);
};

const handleEditLesson = function () {
  handleSaveLessonEditHistory();
  const routeParams = {
    resourceDomain: RESOURCE_DOMAIN_PATH.MY_LIBRARY,
    lessonUuid: newLessonUuid.value,
    mode: "edit",
  };
  const route = router.resolve({
    name: "EditorCover",
    params: routeParams,
  });
  window.location = route.href;
};

const handleSaveLessonEditHistory = function () {
  lessonEditHistoryApi.saveLessonEditHistory(newLessonUuid.value).then((response) => {
    if (response.code !== "ok") {
      passiveApiFailHandler();
    }
  }, passiveApiFailHandler);
};

// Created
isLoading.value = true;
Promise.all([loadLocaleMessages("my-drive-aria-label"), loadLocaleMessages("my-drive")]).then(
  () => {
    isLoading.value = false;
    if (!isDefined(props.params.customTitle)) {
      title.value = t("make_copy_resources_title_placeholder", {
        item: props.params.resources[0].title,
      });
      titles.value.push(
        t("make_copy_resources_title", {
          item: props.params.resources[0].title,
        }),
      );
    } else {
      titles.value.push(props.params.customTitle);
    }
    titleInput.value = t("resources_title_copy", {
      item: props.params.resources[0].title,
    });
  },
);

onBeforeUnmount(() => {
  if (!vm.readingTimer) return;
  clearInterval(vm.readingTimer);
});

vmExpose({
  itemType,
  handleEditLesson,
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";

.resource-copy-content-subpage {
  .cv-search {
    background-color: $ui-background;
    .bx--search-input,
    .filter-selection {
      background-color: inherit;
      &:hover {
        background-color: $hover-ui;
      }
    }
  }
}
</style>
